import { FORTA_ERROR, FORTA_ORANGE, FORTA_SUCCESS, FORTA_WHITE } from '../../../../utils/colors';

export const ClaimStatus = {
  ON_HOLD: 'On Hold',
  GENERATED: 'Generated',
  FULL_DENIAL: 'Full Denial',
  IN_APPEALS: 'In Appeals',
  REJECTED_AND_RESUBMITTED: 'Rejected & Resubmitted',
  REJECTED_NEEDS_APPEAL: 'Rejected, Needs Appeal',
  REJECTED_NEEDS_RESUBMISSION: 'Rejected, Needs Resubmission',
  REJECTED_NOT_RESUBMITTED: 'Rejected, Not Resubmitted',
  APPROVED: 'Approved',
  SUBMITTED: 'Submitted',
  UNBILLABLE: 'Unbillable',
  APPROVED_RECOUPED: 'Approved - Recouped',
  APPROVED_RECOUPED_RESUBMITTED: 'Approved - Recouped & Resubmitted',
} as const;

export type ClaimStatusType = (typeof ClaimStatus)[keyof typeof ClaimStatus];

export const RECOUPMENT_PROBABILITY_LABELS = {
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
} as const;

export type RecoupmentProbabilityType = keyof typeof RECOUPMENT_PROBABILITY_LABELS;

export const RECOUPMENT_PROBABILITY_OPTIONS = Object.entries(RECOUPMENT_PROBABILITY_LABELS).map(
  ([value, label]) => ({ value, label }),
);

export const RECOUPMENT_REASON_LABELS = {
  OVER_AUTHD_HOURS: "Over auth'd hours",
  AUTH_INCORRECT: 'Auth incorrect',
  MODIFIER_ISSUE_NON_AUTH: 'Modifier issue (non auth related)',
  CREDENTIALING_ISSUE: 'Credentialing issue',
  NOT_APPLICABLE: '#N/A',
  AUTH_ISSUE_MODIFIER: 'Auth Issue - Modifier',
  COMPLIANCE_VIOLATION: 'Compliance Violation',
  MODIFIER_ISSUE: 'Modifier Issue',
  PAYOR_ISSUE_BCBS: 'Payor Issue - BCBS',
  RATE_ISSUE: 'Rate issue',
  SUBMISSION_ISSUE: 'Submission issue',
  OVERPAYMENT: 'Overpayment',
  COB: 'COB',
  UNITS_EXCEEDED_32: 'Units Exceeded 32 units',
  NO_SCA: 'No SCA',
} as const;

export type RecoupmentReasonType = keyof typeof RECOUPMENT_REASON_LABELS;

export const RECOUPMENT_REASON_OPTIONS = Object.entries(RECOUPMENT_REASON_LABELS).map(
  ([value, label]) => ({ value, label }),
);

export const ClaimRejectedStatus = {
  REJECTED_AND_RESUBMITTED: 'Rejected & Resubmitted',
  REJECTED_NEEDS_APPEAL: 'Rejected, Needs Appeal',
  REJECTED_NEEDS_RESUBMISSION: 'Rejected, Needs Resubmission',
  REJECTED_NOT_RESUBMITTED: 'Rejected, Not Resubmitted',
  FULL_DENIAL: 'Full Denial',
  UNBILLABLE: 'Unbillable',
  APPROVED_RECOUPED: 'Approved - Recouped',
  APPROVED_RECOUPED_RESUBMITTED: 'Approved - Recouped & Resubmitted',
} as const;

export type ClaimRejectedStatus =
  (typeof ClaimRejectedStatus)[keyof typeof ClaimRejectedStatus];

export const ClaimRejectionReasonV2 = {
  INACTIVE_INSURANCE: 'Inactive insurance',
  NOT_BY_BOBBER: 'Not by bobber',
  PA_GAP: 'PA Gap',
  CODE_NOT_BILLABLE: 'Code not billable',
  INCORECT_PAYOR_WRONG_PORTAL: 'Incorrect payor - wrong portal',
  INCORECT_PAYOR_WRONG_ID: 'Incorrect payor - wrong ID',
  INCORECT_DATA_NPI: 'Incorrect data - NPI',
  INCORRECT_DATA_MODIFIER: 'Incorrect Data - Modifier',
  INCORECT_DATA_ENTITY: 'Incorrect data - Entity',
  INCORECT_DATA_WRONG_BCBA: 'Incorrect data - Wrong BCBA',
  INCORECT_DATA_OTHER: 'Incorrect data - Other',
  C_AND_C_NEEDS_PA: 'C&C - Needs PA',
  C_AND_C_NEEDS_CONTRACT_SCA: 'C&C - Needs Contract / SCA',
  C_AND_C_NEEDS_CREDENTIALING: 'C&C - Needs Credentialing',
  C_AND_C_NEEDS_BOBBING: 'C&C - Needs Bobbing',
  C_AND_C_NO_RBT: 'C&C - No RBT',
  C_AND_C_MODEL_DENIAL: 'C&C - Model Denial',
  CODES_NOT_BILLABLE: 'Codes Not Billable',
  SUB_ERROR_TIMELY_FILING: 'Sub Error - Timely Filing',
  SUB_ERROR_CLAIM_NOT_FOUND: 'Sub Error - Claim Not Found',
  SUB_ERROR_DUPLICATE_SUBMISSION: 'Sub Error - Duplicate Submission',
  SUB_ERROR_EXCEEDED_FILING_FREQUENCY: 'Sub Error - Exceeded Filing Frequency',
  MISSING_DOC_NEEDS_MEDICAL_RECORDS: 'Missing Doc - Needs Medical Records',
  MISSING_DOC_MEDICAL_NECESSITY: 'Missing Doc - Medical Necessity',
  MISSING_DOC_NEED_PROVIDER_INFORMATION: 'Missing Doc - Need Provider Information',
  MISSING_DOC_NEED_W9: 'Missing Doc - Need W9',
  EXCEEDED_APPROVED_UNITS: 'C&C - Exceeded/Exhausted approved units',
  LOST_INSURANCE: 'Lost Insurance',
  RECOUPED: 'Recouped - Claim Resubmission Needed',
} as const;

export const ClaimStatusColor = {
  'In Process': '#8EECF5',
  'On Hold': '#F1C0E8',
  Generated: '#98F5E1',
  Approved: FORTA_SUCCESS,
  'Full Denial': FORTA_ERROR,
  'In Appeals': '#90DBF4',
  'Partial Payment': '#CFBAF0',
  'Rejected & Resubmitted': '#A3C4F3',
  'Rejected, Needs Appeal': '#FDE4CF',
  'Rejected, Needs Resubmission': '#FBF8CC',
  'Rejected, Not Resubmitted': '#FFCFD2',
  Submitted: '#B9FBC0',
  Unbillable: FORTA_ORANGE,
  'Approved - Recouped': FORTA_ORANGE,
  'Approved - Recouped & Resubmitted': FORTA_ORANGE,
} as const;

export const ClaimStatusFontColor = {
  'In Process': '#097B86',
  'On Hold': '#BB089A',
  Generated: '#078369',
  Approved: FORTA_WHITE,
  'Full Denial': FORTA_WHITE,
  'In Appeals': '#055874',
  'Partial Payment': '#6214DD',
  'Rejected & Resubmitted': '#0A4292',
  'Rejected, Needs Appeal': '#F87100',
  'Rejected, Needs Resubmission': '#F87100',
  'Rejected, Not Resubmitted': '#D3000D',
  Submitted: '#009210',
  Unbillable: FORTA_WHITE,
  'Approved - Recouped': FORTA_WHITE,
  'Approved - Recouped & Resubmitted': FORTA_WHITE,
} as const;
